import { createTheme } from "@mui/material/styles";
import { rawColor, colors } from "../constants/colors";

export const lightBlueTheme = createTheme({
  name: "lightBlue",
  palette: {
    mode: "light",
    primary: { main: rawColor.lightBlue.main },
    secondary: { main: rawColor.lightBlue.secondary },
    background: {
      default: rawColor.lightBlue.background,
      paper: rawColor.lightBlue.paper,
    },
    text: {
      primary: rawColor.lightBlue.text.primary,
      secondary: rawColor.lightBlue.text.secondary,
    },
    action: {
      hover: rawColor.lightBlue.sidebar.hover,
    },
    error: { main: rawColor.status.error.light },
    warning: { main: rawColor.status.warning },
    success: { main: rawColor.status.active.main },
    divider: rawColor.lightBlue.divider,
  },
  sidebar: {
    base: colors.sidebar.lightBlue.base,
    hover: colors.sidebar.lightBlue.hover,
    selected: colors.sidebar.lightBlue.selected,
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.sidebar.base,
          borderRight: `1px solid ${theme.palette.divider}`,
          "& .MuiListItemIcon-root": {
            color: rawColor.lightBlue.text.secondary,
          },
          "& .MuiListItemText-root": {
            color: rawColor.lightBlue.text.primary,
          },
          "& .MuiListItemButton-root.Mui-selected": {
            backgroundColor: theme.sidebar.selected,
            "& .MuiListItemIcon-root": {
              color: rawColor.lightBlue.main,
            },
            "& .MuiListItemText-root": {
              color: rawColor.lightBlue.main,
            },
          },
          "& .MuiListItemButton-root:hover": {
            backgroundColor: theme.sidebar.hover,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: colors.table.lightBlue.cell,
          color: rawColor.lightBlue.text.primary,
        },
        head: {
          backgroundColor: colors.table.lightBlue.header,
          color: colors.table.lightBlue.headerText,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: rawColor.lightBlue.paper,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h5: {
          color: rawColor.lightBlue.text.primary,
        },
        h6: {
          color: rawColor.lightBlue.text.primary,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: rawColor.lightBlue.text.primary,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: rawColor.lightBlue.text.secondary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: "currentColor",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: rawColor.lightBlue.text.secondary,
          "& .MuiInputBase-input": {
            color: rawColor.lightBlue.text.secondary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: rawColor.lightBlue.text.secondary,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.lightBlue.divider,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.lightBlue.text.secondary,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.lightBlue.main,
          },
          "& .MuiOutlinedInput-input": {
            color: rawColor.lightBlue.text.secondary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: rawColor.lightBlue.text.secondary,
          "&.Mui-focused": {
            color: rawColor.lightBlue.main,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: rawColor.lightBlue.text.secondary,
        },
        select: {
          color: rawColor.lightBlue.text.secondary,
        },
        selectLabel: {
          color: rawColor.lightBlue.text.secondary,
        },
        selectIcon: {
          color: rawColor.lightBlue.text.secondary,
        },
        displayedRows: {
          color: rawColor.lightBlue.text.secondary,
        },
        actions: {
          color: rawColor.lightBlue.text.secondary,
          "& .MuiSvgIcon-root": {
            color: rawColor.lightBlue.text.secondary,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiAutocomplete-option": {
            color: rawColor.lightBlue.text.secondary,
            "&:hover": {
              backgroundColor: rawColor.lightBlue.sidebar.hover,
            },
            '&[aria-selected="true"]': {
              backgroundColor: rawColor.lightBlue.sidebar.selected,
              color: rawColor.lightBlue.main,
              "&:hover": {
                backgroundColor: rawColor.lightBlue.sidebar.selected,
              },
            },
          },
          "& .MuiAutocomplete-listbox": {
            backgroundColor: rawColor.lightBlue.paper,
            "& .MuiAutocomplete-option": {
              padding: "8px 16px",
            },
          },
          "& .MuiAutocomplete-option .MuiTypography-root": {
            color: "inherit",
          },
          "& .MuiAutocomplete-option .MuiListItemText-root": {
            color: "inherit",
          },
        },
        paper: {
          backgroundColor: rawColor.lightBlue.paper,
          "& .MuiAutocomplete-option": {
            color: rawColor.lightBlue.text.secondary,
          },
        },
        listbox: {
          "& .MuiAutocomplete-option": {
            color: rawColor.lightBlue.text.secondary,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        ".select__control": {
          backgroundColor: `${rawColor.lightBlue.paper} !important`,
          borderColor: `${rawColor.lightBlue.divider} !important`,
          "&:hover": {
            borderColor: `${rawColor.lightBlue.text.secondary} !important`,
          },
          "&--is-focused": {
            borderColor: `${rawColor.lightBlue.main} !important`,
            boxShadow: `0 0 0 1px ${rawColor.lightBlue.main} !important`,
          },
        },
        ".select__menu": {
          backgroundColor: `${rawColor.lightBlue.paper} !important`,
          border: `1px solid ${rawColor.lightBlue.divider} !important`,
        },
        ".select__option": {
          backgroundColor: `${rawColor.lightBlue.paper} !important`,
          color: `${rawColor.lightBlue.text.secondary} !important`,
          "&:hover": {
            backgroundColor: `${rawColor.lightBlue.sidebar.hover} !important`,
          },
          "&--is-focused": {
            backgroundColor: `${rawColor.lightBlue.sidebar.hover} !important`,
          },
          "&--is-selected": {
            backgroundColor: `${rawColor.lightBlue.sidebar.selected} !important`,
            color: `${rawColor.lightBlue.main} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.lightBlue.sidebar.selected} !important`,
            },
          },
        },
        ".select__multi-value": {
          backgroundColor: `${rawColor.lightBlue.sidebar.hover} !important`,
          "&__label": {
            color: `${rawColor.lightBlue.text.secondary} !important`,
          },
          "&__remove": {
            color: `${rawColor.lightBlue.text.secondary} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.lightBlue.sidebar.selected} !important`,
              color: `${rawColor.lightBlue.main} !important`,
            },
          },
        },
        ".select__placeholder": {
          color: `${rawColor.lightBlue.text.secondary} !important`,
        },
        ".select__single-value": {
          color: `${rawColor.lightBlue.text.secondary} !important`,
        },
        ".select__input": {
          color: `${rawColor.lightBlue.text.secondary} !important`,
        },
        ".select__indicator": {
          color: `${rawColor.lightBlue.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.lightBlue.main} !important`,
          },
        },
        ".select__indicator-separator": {
          backgroundColor: `${rawColor.lightBlue.divider} !important`,
        },
        ".select__clear-indicator": {
          color: `${rawColor.lightBlue.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.lightBlue.main} !important`,
          },
        },
        ".select__dropdown-indicator": {
          color: `${rawColor.lightBlue.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.lightBlue.main} !important`,
          },
        },
      },
    },
  },
});
